import { useMemo } from 'react';
import { ExternalLink, Phone, Copy, Reply, Image as ImageIcon, FileText, Video } from 'lucide-react';
import {
  parseVariableDefs,
  CONTACT_FIELD_LABELS,
  type VariableDef,
} from '@kalkuel-inbox/shared';
import type { TemplateButton, TemplateHeaderType } from '../types';

/**
 * Wiederverwendbare WhatsApp-Look-Vorschau einer Vorlage — als Chat-Bubble mit
 * optionalem Header (Text oder Medien-Thumbnail), Body (mit Platzhalter-Chips oder
 * eingesetzten Werten), Footer und typisierten Buttons.
 *
 * Genutzt sowohl in der Vorlagen-Galerie (Auswahl-Dialog) als auch im Editor
 * (Live-Vorschau). Bewusst ein schlankes Prop-Interface (`TemplatePreviewData`)
 * statt des vollen `Template`-Typs, damit auch der Editor-Zwischenzustand passt.
 */
export interface TemplatePreviewData {
  /** Anzeigename (optional — wird als Überschrift über der Bubble gezeigt). */
  displayName?: string;
  body: string;
  headerType?: TemplateHeaderType | '' | null;
  headerText?: string | null;
  /** Dateiname eines hochgeladenen Beispiel-Mediums (Medien-Header). */
  headerMediaFilename?: string | null;
  footer?: string | null;
  buttons?: TemplateButton[] | null;
  /** Roh-`variableDefs` (JSON) — für Platzhalter-Labels, wenn keine `filledVars` da sind. */
  variableDefs?: unknown;
}

interface TemplatePreviewProps {
  template: TemplatePreviewData;
  /**
   * Optionale eingesetzte Werte je 1-basiertem Platzhalter-Index. Ist ein Wert
   * gesetzt, wird er statt des Platzhalter-Chips angezeigt (z.B. Submit-Vorschau).
   */
  filledVars?: Record<number, string>;
}

type BodySegment = { type: 'text'; value: string } | { type: 'var'; index: number };

/** Zerlegt einen Body in Text- und Platzhalter-Segmente ({{n}}). */
export function splitBody(body: string): BodySegment[] {
  const src = body ?? '';
  const segments: BodySegment[] = [];
  const re = /\{\{\s*(\d+)\s*\}\}/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(src)) !== null) {
    if (m.index > last) segments.push({ type: 'text', value: src.slice(last, m.index) });
    segments.push({ type: 'var', index: Number(m[1]) });
    last = m.index + m[0].length;
  }
  if (last < src.length) segments.push({ type: 'text', value: src.slice(last) });
  return segments;
}

/** Label für einen unausgefüllten Platzhalter {{n}} aus den VariableDefs. */
function placeholderLabel(idx: number, defs: VariableDef[]): string {
  const def = defs.find((d) => d.index === idx);
  // CONTACT_FIELD_LABELS kann undefined sein, falls das Backend ein contactField
  // außerhalb des bekannten Union-Typs liefert (parseVariableDefs castet tolerant).
  if (def?.source === 'CONTACT' && def.contactField) {
    return CONTACT_FIELD_LABELS[def.contactField] ?? def.label ?? `Variable ${idx}`;
  }
  if (def?.label) return def.label;
  return `Variable ${idx}`;
}

/**
 * Rendert eine Vorlage als WhatsApp-artige Chat-Bubble: optionaler Header,
 * Body mit Platzhalter-Chips (oder eingesetzten Werten), Footer und Buttons.
 */
export function TemplatePreview({ template, filledVars }: TemplatePreviewProps) {
  const defs = useMemo(() => parseVariableDefs(template.variableDefs), [template.variableDefs]);
  const segments = useMemo(() => splitBody(template.body), [template.body]);

  return (
    <div className="space-y-2">
      {template.displayName && (
        <div className="text-sm font-semibold text-kalkuel-primary">{template.displayName}</div>
      )}

      {/* Chat-Bubble */}
      <div className="max-w-full rounded-xl rounded-tl-sm bg-white p-3 shadow-brand">
        {template.headerType && (
          <HeaderPreview
            type={template.headerType}
            text={template.headerText ?? null}
            mediaFilename={template.headerMediaFilename ?? null}
          />
        )}

        <div className="whitespace-pre-wrap break-words text-sm text-kalkuel-primary">
          {segments.map((seg, i) => {
            if (seg.type === 'text') return <span key={i}>{seg.value}</span>;
            const filled = filledVars?.[seg.index];
            if (filled && filled.trim()) {
              return <span key={i}>{filled}</span>;
            }
            return (
              <span
                key={i}
                className="mx-0.5 inline-flex items-center rounded-pill bg-kalkuel-accent-soft px-1.5 py-0.5 text-[11px] font-medium text-kalkuel-accent2"
                title={`Platzhalter {{${seg.index}}}`}
              >
                {placeholderLabel(seg.index, defs)}
              </span>
            );
          })}
        </div>

        {template.footer && (
          <div className="mt-2 text-[11px] text-kalkuel-muted">{template.footer}</div>
        )}
      </div>

      {/* Buttons als Pills mit Typ-Icon */}
      {template.buttons && template.buttons.filter(isShownButton).length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {template.buttons.filter(isShownButton).map((b, i) => (
            <span
              key={i}
              className="inline-flex items-center gap-1 rounded-pill border border-kalkuel-border bg-white px-3 py-1 text-xs font-medium text-kalkuel-accent2"
            >
              <ButtonIcon type={b.type} />
              {buttonLabel(b)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/** Ein Button wird in der Vorschau gezeigt, wenn er eine Beschriftung hat (COPY_CODE ohne Text ok). */
function isShownButton(b: TemplateButton): boolean {
  return b.type === 'COPY_CODE' || !!(b.text ?? '').trim();
}

/** Anzeigetext eines Buttons (COPY_CODE ohne eigenen Text → generisches Label). */
function buttonLabel(b: TemplateButton): string {
  if ((b.text ?? '').trim()) return b.text;
  if (b.type === 'COPY_CODE') return 'Code kopieren';
  return '';
}

function ButtonIcon({ type }: { type: TemplateButton['type'] }) {
  switch (type) {
    case 'URL':
      return <ExternalLink size={11} aria-hidden />;
    case 'PHONE_NUMBER':
      return <Phone size={11} aria-hidden />;
    case 'COPY_CODE':
      return <Copy size={11} aria-hidden />;
    case 'QUICK_REPLY':
    default:
      return <Reply size={11} aria-hidden />;
  }
}

function HeaderPreview({
  type,
  text,
  mediaFilename,
}: {
  type: TemplateHeaderType;
  text: string | null;
  mediaFilename: string | null;
}) {
  if (type === 'TEXT') {
    return <div className="mb-2 text-sm font-bold text-kalkuel-primary">{text}</div>;
  }
  const Icon = type === 'IMAGE' ? ImageIcon : type === 'VIDEO' ? Video : FileText;
  const label =
    mediaFilename ??
    (type === 'IMAGE' ? 'Bild-Header' : type === 'VIDEO' ? 'Video-Header' : 'Dokument-Header');
  return (
    <div className="mb-2 flex h-16 items-center justify-center gap-1.5 rounded-md border border-dashed border-kalkuel-border bg-kalkuel-surface px-2 text-[11px] font-medium text-kalkuel-muted">
      <Icon size={16} aria-hidden />
      <span className="truncate">{label}</span>
    </div>
  );
}
